import { fade, palette } from './colors';
import { createShadowAnimationStack } from './shadow_stacks';

// Separate shadows, not one joined string: `createShadowAnimationStack` counts entries to line
// states up, and a joined string counts as one.
const focusRingLight = [
  `0 0 0 1px ${fade(palette.brand600, 0.7)}`,
  `0 0 0 3px ${fade(palette.brand500, 0.7)}`,
];
const focusRingDark = [
  `0 0 0 1px ${fade(palette.brand100, 0.7)}`,
  `0 0 0 4px ${fade(palette.brand200, 0.7)}`,
];

const control = {
  initialLight: [
    `0 1px 1px 0 ${fade(palette.gray900, 0.08)}`,
    `0 1px 3px 0 ${fade(palette.gray900, 0.12)}`,
    `inset 0 1px 0 0 ${fade(palette.white, 0.1)}`,
  ],
  hoveredLight: [
    `0 1px 1px 0 ${fade(palette.gray900, 0.12)}`,
    `0 1px 3px 0 ${fade(palette.gray900, 0.16)}`,
    `inset 0 1px 0 0 ${fade(palette.white, 0.1)}`,
  ],
  disabledLight: [
    `0 1px 1px 0 ${fade(palette.gray900, 0.04)}`,
    `0 1px 3px 0 ${fade(palette.gray900, 0.08)}`,
    `inset 0 1px 0 0 ${fade(palette.white, 0.06)}`,
  ],
  initialDark: [
    `0 1px 1px 0 ${fade(palette.gray900, 0.8)}`,
    `0 1px 3px 0 ${fade(palette.gray900, 0.7)}`,
    `inset 0 1px 0 0 ${fade(palette.white, 0.1)}`,
  ],
  hoveredDark: [
    `0 1px 1px 0 ${fade(palette.gray900, 0.86)}`,
    `0 1px 3px 0 ${fade(palette.gray900, 0.76)}`,
    `inset 0 1px 0 0 ${fade(palette.white, 0.1)}`,
  ],
  disabledDark: [
    `0 1px 1px 0 ${fade(palette.gray900, 0.6)}`,
    `0 1px 3px 0 ${fade(palette.gray900, 0.5)}`,
    `inset 0 1px 0 0 ${fade(palette.white, 0.06)}`,
  ],
};

/** Lighter elevation for raised/secondary-vivid controls only */
const raisedElevationLight = {
  initial: [
    `0 1px 1px 0 ${fade(palette.gray900, 0.03)}`,
    `0 1px 3px 0 ${fade(palette.gray900, 0.05)}`,
    `inset 0 1px 0 0 ${fade(palette.white, 0.08)}`,
  ],
  hovered: [
    `0 1px 1px 0 ${fade(palette.gray900, 0.05)}`,
    `0 1px 3px 0 ${fade(palette.gray900, 0.07)}`,
    `inset 0 1px 0 0 ${fade(palette.white, 0.12)}`,
  ],
  disabled: [
    `0 1px 1px 0 ${fade(palette.gray900, 0.02)}`,
    `0 1px 3px 0 ${fade(palette.gray900, 0.03)}`,
    `inset 0 1px 0 0 ${fade(palette.white, 0.04)}`,
  ],
};

const raisedElevationDark = {
  initial: [
    `0 1px 1px 0 ${fade(palette.gray900, 0.4)}`,
    `0 1px 3px 0 ${fade(palette.gray900, 0.35)}`,
    `inset 0 1px 0 0 ${fade(palette.white, 0.08)}`,
  ],
  hovered: [
    `0 1px 1px 0 ${fade(palette.gray900, 0.45)}`,
    `0 1px 3px 0 ${fade(palette.gray900, 0.38)}`,
    `inset 0 1px 0 0 ${fade(palette.white, 0.12)}`,
  ],
  disabled: [
    `0 1px 1px 0 ${fade(palette.gray900, 0.35)}`,
    `0 1px 3px 0 ${fade(palette.gray900, 0.3)}`,
    `inset 0 1px 0 0 ${fade(palette.white, 0.04)}`,
  ],
};

/** The connection shadow's soft layers, without the 1px ring — `SlotChip` recolours that ring. */
export const connectionDrops = {
  light: [
    `0 1px 2px 0 ${fade(palette.gray900, 0.05)}`,
    `0 3px 5px 0 ${fade(palette.gray900, 0.03)}`,
  ],
  dark: [`0 1px 2px 0 ${fade(palette.gray700, 0.5)}`, `0 3px 5px 0 ${fade(palette.gray700, 0.3)}`],
};

/**
 * A focused control wears the halo over its *hovered* elevation, matching how focus adopts the
 * hover background and stroke elsewhere. Keeping the elevation shadows in the same slots either
 * side of the transition is what lets the halo expand out of the edge instead of the drop shadow
 * sliding up to become one. Controls with no elevation take the halo alone.
 */
const focused = (elevation: Array<string>, ring: Array<string>) => [...elevation, ...ring];

export const shadowParts = {
  none: [],

  focusRingLight,
  focusRingDark,

  // Card
  cardLight: [
    `0 0 0 1px ${fade(palette.gray900, 0.08)}`,
    `0 2px 0 0 ${fade(palette.gray900, 0.05)}`,
    `0 2px 2px 0 ${fade(palette.gray900, 0.05)}`,
  ],
  cardDark: [
    `0 0 0 1px ${fade(palette.gray700, 0.9)}`,
    `0 3px 0 0 ${fade(palette.gray900, 0.9)}`,
    `0 3px 2px 0 ${fade(palette.gray900, 0.9)}`,
  ],

  // Destructive vivid control, light (1px stroke → border on element)
  controlVividDestructiveInitialLight: raisedElevationLight.initial,
  controlVividDestructiveHoveredLight: raisedElevationLight.hovered,
  controlVividDestructiveFocusedLight: focused(raisedElevationLight.hovered, focusRingLight),
  controlVividDestructiveDisabledLight: raisedElevationLight.disabled,

  // Destructive muted control, light
  controlMutedDestructiveFocusedLight: focusRingLight,

  // Destructive control, dark
  controlVividDestructiveInitialDark: raisedElevationDark.initial,
  controlVividDestructiveHoveredDark: raisedElevationDark.hovered,
  controlVividDestructiveFocusedDark: focused(raisedElevationDark.hovered, focusRingDark),
  controlVividDestructiveDisabledDark: raisedElevationDark.disabled,

  // Destructive muted control, dark
  controlMutedDestructiveFocusedDark: focusRingDark,

  // Primary control, light
  controlVividPrimaryInitialLight: raisedElevationLight.initial,
  controlVividPrimaryHoveredLight: raisedElevationLight.hovered,
  controlVividPrimaryFocusedLight: focused(raisedElevationLight.hovered, focusRingLight),
  controlVividPrimaryDisabledLight: raisedElevationLight.disabled,

  // Primary muted control, light
  controlMutedPrimaryFocusedLight: focusRingLight,

  // Primary control, dark
  controlVividPrimaryInitialDark: raisedElevationDark.initial,
  controlVividPrimaryHoveredDark: raisedElevationDark.hovered,
  controlVividPrimaryFocusedDark: focused(raisedElevationDark.hovered, focusRingDark),
  controlVividPrimaryDisabledDark: raisedElevationDark.disabled,

  // Primary muted control, dark
  controlMutedPrimaryFocusedDark: focusRingDark,

  // Secondary vivid control, light
  controlSecondaryVividInitialLight: raisedElevationLight.initial,
  controlSecondaryVividHoveredLight: raisedElevationLight.hovered,
  controlSecondaryVividFocusedLight: focused(raisedElevationLight.hovered, focusRingLight),
  controlSecondaryVividDisabledLight: raisedElevationLight.disabled,

  // Secondary vivid control, dark
  controlSecondaryVividInitialDark: raisedElevationDark.initial,
  controlSecondaryVividHoveredDark: raisedElevationDark.hovered,
  controlSecondaryVividFocusedDark: focused(raisedElevationDark.hovered, focusRingDark),
  controlSecondaryVividDisabledDark: raisedElevationDark.disabled,

  // Secondary control, light
  controlSecondaryMutedFocusedLight: focusRingLight,

  // Secondary control, dark
  controlSecondaryMutedFocusedDark: focusRingDark,

  // Internal control, light
  controlInternalFocusedLight: focusRingLight,

  // Internal control, dark
  controlInternalFocusedDark: focusRingDark,

  // Raised control, light
  controlRaisedInitialLight: control.initialLight,
  controlRaisedHoveredLight: control.hoveredLight,
  controlRaisedFocusedLight: focused(control.hoveredLight, focusRingLight),
  controlRaisedDisabledLight: control.disabledLight,

  // Raised control, dark
  controlRaisedInitialDark: control.initialDark,
  controlRaisedHoveredDark: control.hoveredDark,
  controlRaisedFocusedDark: focused(control.hoveredDark, focusRingDark),
  controlRaisedDisabledDark: control.disabledDark,

  // Fields
  fieldInitialLight: [
    `0 2px 1px 0 ${fade(palette.gray900, 0.08)}`,
    `0 2px 3px 0 ${fade(palette.gray900, 0.05)}`,
    `0 0 0 1px ${fade(palette.gray600, 0.3)}`,
  ],
  fieldHoveredLight: [
    `0 2px 1px 0 ${fade(palette.gray900, 0.12)}`,
    `0 2px 3px 0 ${fade(palette.gray900, 0.18)}`,
    `0 0 0 1px ${fade(palette.gray600, 0.4)}`,
  ],
  fieldFocusedLight: [
    `0 0 0 1px ${fade(palette.controlStrokeBaseFocusedLight, 0.6)}`,
    `inset 0 0 0 1px ${fade(palette.controlStrokeBaseFocusedLight, 0.6)}`,
    ...focusRingLight,
  ],
  fieldDisabledLight: [
    `0 2px 1px 0 ${fade(palette.gray900, 0.08)}`,
    `0 2px 3px 0 ${fade(palette.gray900, 0.05)}`,
    `0 0 0 1px rgba(177, 181, 199, 1)`,
  ],
  fieldErrorLight: [
    `0 2px 1px 0 ${fade(palette.gray900, 0.08)}`,
    `0 2px 3px 0 ${fade(palette.gray900, 0.05)}`,
    `0 0 0 1.5px ${fade(palette.red600, 0.8)}`,
  ],
  fieldInitialDark: [
    `0 2px 1px 0 ${fade(palette.gray900, 0.25)}`,
    `0 2px 3px 0 ${fade(palette.gray900, 0.4)}`,
    `0 0 0 1px ${fade(palette.gray900, 0.5)}`,
  ],
  fieldHoveredDark: [
    `0 2px 1px 0 ${fade(palette.gray900, 0.4)}`,
    `0 2px 3px 0 ${fade(palette.gray900, 0.5)}`,
    `0 0 0 1px ${fade(palette.gray900, 0.7)}`,
  ],
  fieldFocusedDark: [
    `0 0 0 1px ${fade(palette.controlStrokeBaseFocusedDark, 0.6)}`,
    `inset 0 0 0 1px ${fade(palette.controlStrokeBaseFocusedDark, 0.6)}`,
    ...focusRingDark,
  ],
  fieldDisabledDark: [
    `0 2px 1px 0 ${fade(palette.gray900, 0.02)}`,
    `0 2px 3px 0 ${fade(palette.gray900, 0.05)}`,
    `0 0 0 1px ${fade(palette.gray900, 0.4)}`,
  ],
  fieldErrorDark: [
    `0 2px 1px 0 ${fade(palette.gray900, 0.25)}`,
    `0 2px 3px 0 ${fade(palette.gray900, 0.4)}`,
    `0 0 0 1px ${fade(palette.red300, 0.8)}`,
  ],

  // Device
  deviceLight: [
    `0 0 0 1px ${fade(palette.gray900, 0.08)}`,
    `0 1px 1px 0 ${fade(palette.gray900, 0.08)}`,
    `0 2px 3px 0 ${fade(palette.gray900, 0.05)}`,
  ],
  deviceDark: [
    `0 0 0 1px ${fade(palette.gray600, 0.8)}`,
    `0 1px 1px 0 ${fade(palette.gray600, 0.8)}`,
    `0 2px 3px 0 ${fade(palette.gray600, 0.7)}`,
  ],

  // Connection
  connectionLight: [`0 0 0 1px ${palette.strokeNeutralLight}`, ...connectionDrops.light],
  connectionDark: [`0 0 0 1px ${palette.strokeNeutralDark}`, ...connectionDrops.dark],

  // Layer
  layerLight: [
    `-1px 0 1px 0 ${fade(palette.gray800, 0.04)}`,
    `-2px 0 3px 0 ${fade(palette.gray800, 0.04)}`,
  ],

  layerDark: [
    `-1px 0 0 0 ${fade(palette.gray700, 0.8)}`,
    `-1px 0 1px 0 ${fade(palette.gray900, 0.8)}`,
    `-2px 0 3px 0 ${fade(palette.gray900, 0.8)}`,
  ],

  // Stuck
  stuckLight: [
    `0 1px 1px 0 ${fade(palette.gray800, 0.04)}`,
    `0 2px 3px 0 ${fade(palette.gray800, 0.04)}`,
  ],

  stuckDark: [
    `0 1px 0 0 ${fade(palette.gray700, 0.8)}`,
    `0 1px 1px 0 ${fade(palette.gray900, 0.8)}`,
    `0 2px 3px 0 ${fade(palette.gray900, 0.8)}`,
  ],

  // Overlay
  overlayLight: [
    `0 3px 5px 0 ${fade(palette.gray900, 0.05)}`,
    `0 2px 3px 0 ${fade(palette.gray900, 0.05)}`,
    `0 2px 0 0 ${fade(palette.gray900, 0.05)}`,
    `0 0 0 1px ${fade(palette.gray900, 0.08)}`,
  ],
  overlayDark: [
    `0 3px 5px 0 ${fade(palette.gray700, 0.3)}`,
    `0 2px 3px 0 ${fade(palette.gray700, 0.5)}`,
    `0 2px 0 0 ${fade(palette.gray700, 0.2)}`,
    `0 0 0 1px ${fade(palette.gray700, 0.9)}`,
  ],
  overlayAppLight: [
    `inset 0 1px 0 0 ${palette.strokeNeutralLight}`,
    `0 3px 5px 0 ${fade(palette.gray900, 0.05)}`,
    `0 2px 3px 0 ${fade(palette.gray900, 0.05)}`,
    `0 2px 0 0 ${fade(palette.gray900, 0.05)}`,
    `0 0 0 1px ${fade(palette.gray900, 0.08)}`,
  ],
  overlayAppDark: [
    `inset 0 1px 0 0 ${palette.strokeNeutralDark}`,
    `0 3px 5px 0 ${fade(palette.gray700, 0.3)}`,
    `0 2px 3px 0 ${fade(palette.gray700, 0.5)}`,
    `0 2px 0 0 ${fade(palette.gray700, 0.2)}`,
    `0 0 0 1px ${fade(palette.gray700, 0.9)}`,
  ],
  overlayAlternativeLight: [
    `0 3px 5px 0 ${fade(palette.gray900, 0.05)}`,
    `0 2px 3px 0 ${fade(palette.gray900, 0.05)}`,
    `0 2px 0 0 ${fade(palette.gray900, 0.05)}`,
    `0 0 0 1px ${fade(palette.strokeAlternativeLight, 0.8)}`,
  ],
  overlayAlternativeDark: [
    `0 3px 5px 0 ${fade(palette.gray700, 0.3)}`,
    `0 2px 3px 0 ${fade(palette.gray700, 0.5)}`,
    `0 2px 0 0 ${fade(palette.gray700, 0.2)}`,
    `0 0 0 1px ${fade(palette.strokeAlternativeDark, 0.9)}`,
  ],
  overlayAttentionLight: [
    `0 3px 5px 0 ${fade(palette.gray900, 0.05)}`,
    `0 2px 3px 0 ${fade(palette.gray900, 0.05)}`,
    `0 2px 0 0 ${fade(palette.gray900, 0.05)}`,
    `0 0 0 1px ${fade(palette.strokeAttentionLight, 0.8)}`,
  ],
  overlayAttentionDark: [
    `0 3px 5px 0 ${fade(palette.gray700, 0.3)}`,
    `0 2px 3px 0 ${fade(palette.gray700, 0.5)}`,
    `0 2px 0 0 ${fade(palette.gray700, 0.2)}`,
    `0 0 0 1px ${fade(palette.strokeAttentionDark, 0.9)}`,
  ],
  overlayBrandLight: [
    `0 3px 5px 0 ${fade(palette.gray900, 0.05)}`,
    `0 2px 3px 0 ${fade(palette.gray900, 0.05)}`,
    `0 2px 0 0 ${fade(palette.gray900, 0.05)}`,
    `0 0 0 1px ${fade(palette.strokeBrandLight, 0.8)}`,
  ],
  overlayBrandDark: [
    `0 3px 5px 0 ${fade(palette.gray700, 0.3)}`,
    `0 2px 3px 0 ${fade(palette.gray700, 0.5)}`,
    `0 2px 0 0 ${fade(palette.gray700, 0.2)}`,
    `0 0 0 1px ${fade(palette.strokeBrandDark, 0.9)}`,
  ],
  overlayNegativeLight: [
    `0 3px 5px 0 ${fade(palette.gray900, 0.05)}`,
    `0 2px 3px 0 ${fade(palette.gray900, 0.05)}`,
    `0 2px 0 0 ${fade(palette.gray900, 0.05)}`,
    `0 0 0 1px ${fade(palette.strokeNegativeLight, 0.8)}`,
  ],
  overlayNegativeDark: [
    `0 3px 5px 0 ${fade(palette.gray700, 0.3)}`,
    `0 2px 3px 0 ${fade(palette.gray700, 0.5)}`,
    `0 2px 0 0 ${fade(palette.gray700, 0.2)}`,
    `0 0 0 1px ${fade(palette.strokeNegativeDark, 0.9)}`,
  ],
  overlayNeutralLight: [
    `0 3px 5px 0 ${fade(palette.gray900, 0.05)}`,
    `0 2px 3px 0 ${fade(palette.gray900, 0.05)}`,
    `0 2px 0 0 ${fade(palette.gray900, 0.05)}`,
    `0 0 0 1px ${fade(palette.strokeNeutralLight, 0.8)}`,
  ],
  overlayNeutralDark: [
    `0 3px 5px 0 ${fade(palette.gray700, 0.3)}`,
    `0 2px 3px 0 ${fade(palette.gray700, 0.5)}`,
    `0 2px 0 0 ${fade(palette.gray700, 0.2)}`,
    `0 0 0 1px ${fade(palette.strokeNeutralDark, 0.9)}`,
  ],
  overlayPositiveLight: [
    `0 3px 5px 0 ${fade(palette.gray900, 0.05)}`,
    `0 2px 3px 0 ${fade(palette.gray900, 0.05)}`,
    `0 2px 0 0 ${fade(palette.gray900, 0.05)}`,
    `0 0 0 1px ${fade(palette.strokePositiveLight, 0.8)}`,
  ],
  overlayPositiveDark: [
    `0 3px 5px 0 ${fade(palette.gray700, 0.3)}`,
    `0 2px 3px 0 ${fade(palette.gray700, 0.5)}`,
    `0 2px 0 0 ${fade(palette.gray700, 0.2)}`,
    `0 0 0 1px ${fade(palette.strokePositiveDark, 0.9)}`,
  ],
  overlayInternalLight: [
    `0 3px 5px 0 ${fade(palette.gray900, 0.05)}`,
    `0 2px 3px 0 ${fade(palette.gray900, 0.05)}`,
    `0 2px 0 0 ${fade(palette.gray900, 0.05)}`,
    `0 0 0 1px ${fade(palette.internalStrokeLight, 0.8)}`,
  ],
  overlayInternalDark: [
    `0 3px 5px 0 ${fade(palette.gray700, 0.3)}`,
    `0 2px 3px 0 ${fade(palette.gray700, 0.5)}`,
    `0 2px 0 0 ${fade(palette.gray700, 0.2)}`,
    `0 0 0 1px ${fade(palette.internalStrokeDark, 0.9)}`,
  ],

  // Overlay thick
  overlayThickLight: [
    `0 3px 5px 0 ${fade(palette.gray900, 0.05)}`,
    `0 2px 3px 0 ${fade(palette.gray900, 0.05)}`,
    `0 2px 0 0 ${fade(palette.gray900, 0.05)}`,
    `0 0 0 2px ${fade(palette.gray900, 0.08)}`,
  ],
  overlayThickDark: [
    `0 3px 5px 0 ${fade(palette.gray700, 0.3)}`,
    `0 2px 3px 0 ${fade(palette.gray700, 0.5)}`,
    `0 2px 0 0 ${fade(palette.gray700, 0.2)}`,
    `0 0 0 2px ${fade(palette.gray700, 0.5)}`,
  ],
  overlayThickAppLight: [
    `inset 0 1px 0 0 ${palette.strokeNeutralLight}`,
    `0 3px 5px 0 ${fade(palette.gray900, 0.05)}`,
    `0 2px 3px 0 ${fade(palette.gray900, 0.05)}`,
    `0 2px 0 0 ${fade(palette.gray900, 0.05)}`,
    `0 0 0 2px ${fade(palette.gray900, 0.08)}`,
  ],
  overlayThickAppDark: [
    `inset 0 1px 0 0 ${palette.strokeNeutralDark}`,
    `0 3px 5px 0 ${fade(palette.gray700, 0.3)}`,
    `0 2px 3px 0 ${fade(palette.gray700, 0.5)}`,
    `0 2px 0 0 ${fade(palette.gray700, 0.2)}`,
    `0 0 0 2px ${fade(palette.gray700, 0.9)}`,
  ],
  overlayThickAlternativeLight: [
    `0 3px 5px 0 ${fade(palette.gray900, 0.05)}`,
    `0 2px 3px 0 ${fade(palette.gray900, 0.05)}`,
    `0 2px 0 0 ${fade(palette.gray900, 0.05)}`,
    `0 0 0 2px ${fade(palette.strokeAlternativeLight, 0.8)}`,
  ],
  overlayThickAlternativeDark: [
    `0 3px 5px 0 ${fade(palette.gray700, 0.3)}`,
    `0 2px 3px 0 ${fade(palette.gray700, 0.5)}`,
    `0 2px 0 0 ${fade(palette.gray700, 0.2)}`,
    `0 0 0 2px ${fade(palette.strokeAlternativeDark, 0.9)}`,
  ],
  overlayThickAttentionLight: [
    `0 3px 5px 0 ${fade(palette.gray900, 0.05)}`,
    `0 2px 3px 0 ${fade(palette.gray900, 0.05)}`,
    `0 2px 0 0 ${fade(palette.gray900, 0.05)}`,
    `0 0 0 2px ${fade(palette.strokeAttentionLight, 0.8)}`,
  ],
  overlayThickAttentionDark: [
    `0 3px 5px 0 ${fade(palette.gray700, 0.3)}`,
    `0 2px 3px 0 ${fade(palette.gray700, 0.5)}`,
    `0 2px 0 0 ${fade(palette.gray700, 0.2)}`,
    `0 0 0 2px ${fade(palette.strokeAttentionDark, 0.9)}`,
  ],
  overlayThickBrandLight: [
    `0 3px 5px 0 ${fade(palette.gray900, 0.05)}`,
    `0 2px 3px 0 ${fade(palette.gray900, 0.05)}`,
    `0 2px 0 0 ${fade(palette.gray900, 0.05)}`,
    `0 0 0 2px ${fade(palette.strokeBrandLight, 0.8)}`,
  ],
  overlayThickBrandDark: [
    `0 3px 5px 0 ${fade(palette.gray700, 0.3)}`,
    `0 2px 3px 0 ${fade(palette.gray700, 0.5)}`,
    `0 2px 0 0 ${fade(palette.gray700, 0.2)}`,
    `0 0 0 2px ${fade(palette.strokeBrandDark, 0.9)}`,
  ],
  overlayThickNegativeLight: [
    `0 3px 5px 0 ${fade(palette.gray900, 0.05)}`,
    `0 2px 3px 0 ${fade(palette.gray900, 0.05)}`,
    `0 2px 0 0 ${fade(palette.gray900, 0.05)}`,
    `0 0 0 2px ${fade(palette.strokeNegativeLight, 0.8)}`,
  ],
  overlayThickNegativeDark: [
    `0 3px 5px 0 ${fade(palette.gray700, 0.3)}`,
    `0 2px 3px 0 ${fade(palette.gray700, 0.5)}`,
    `0 2px 0 0 ${fade(palette.gray700, 0.2)}`,
    `0 0 0 2px ${fade(palette.strokeNegativeDark, 0.9)}`,
  ],
  overlayThickNeutralLight: [
    `0 3px 5px 0 ${fade(palette.gray900, 0.05)}`,
    `0 2px 3px 0 ${fade(palette.gray900, 0.05)}`,
    `0 2px 0 0 ${fade(palette.gray900, 0.05)}`,
    `0 0 0 2px ${fade(palette.strokeNeutralLight, 0.8)}`,
  ],
  overlayThickNeutralDark: [
    `0 3px 5px 0 ${fade(palette.gray700, 0.3)}`,
    `0 2px 3px 0 ${fade(palette.gray700, 0.5)}`,
    `0 2px 0 0 ${fade(palette.gray700, 0.2)}`,
    `0 0 0 2px ${fade(palette.strokeNeutralDark, 0.9)}`,
  ],
  overlayThickPositiveLight: [
    `0 3px 5px 0 ${fade(palette.gray900, 0.05)}`,
    `0 2px 3px 0 ${fade(palette.gray900, 0.05)}`,
    `0 2px 0 0 ${fade(palette.gray900, 0.05)}`,
    `0 0 0 2px ${fade(palette.strokePositiveLight, 0.8)}`,
  ],
  overlayThickPositiveDark: [
    `0 3px 5px 0 ${fade(palette.gray700, 0.3)}`,
    `0 2px 3px 0 ${fade(palette.gray700, 0.5)}`,
    `0 2px 0 0 ${fade(palette.gray700, 0.2)}`,
    `0 0 0 2px ${fade(palette.strokePositiveDark, 0.9)}`,
  ],
  overlayThickInternalLight: [
    `0 3px 5px 0 ${fade(palette.gray900, 0.05)}`,
    `0 2px 3px 0 ${fade(palette.gray900, 0.05)}`,
    `0 2px 0 0 ${fade(palette.gray900, 0.05)}`,
    `0 0 0 2px ${fade(palette.internalStrokeLight, 0.8)}`,
  ],
  overlayThickInternalDark: [
    `0 3px 5px 0 ${fade(palette.gray700, 0.3)}`,
    `0 2px 3px 0 ${fade(palette.gray700, 0.5)}`,
    `0 2px 0 0 ${fade(palette.gray700, 0.2)}`,
    `0 0 0 2px ${fade(palette.internalStrokeDark, 0.9)}`,
  ],

  // Dialog
  modalLight: [
    `0 9px 11px 0 ${fade(palette.gray900, 0.05)}`,
    `0 7px 8px 0 ${fade(palette.gray900, 0.05)}`,
    `0 3px 0 0 ${fade(palette.gray900, 0.04)}`,
    `0 0 0 1px ${fade(palette.gray900, 0.08)}`,
  ],
  modalDark: [
    `0 9px 11px 0 ${fade(palette.gray700, 0.3)}`,
    `0 7px 8px 0 ${fade(palette.gray700, 0.5)}`,
    `0 3px 0 0 ${fade(palette.gray700, 0.2)}`,
    `0 0 0 1px ${fade(palette.gray700, 0.9)}`,
  ],

  // Navbar
  navbarLight: [
    `0 1px 2px 0 ${fade(palette.gray900, 0.04)}`,
    `0 3px 5px 0 ${fade(palette.gray900, 0.05)}`,
    `inset 0 -1px 0 0 ${fade(palette.gray900, 0.12)}`,
  ],
  navbarDark: [
    `0 1px 1px 0 ${fade(palette.gray900, 0.6)}`,
    `0 1px 3px 0 ${fade(palette.gray900, 0.5)}`,
    `inset 0 1px 0 0 ${fade(palette.white, 0.06)}`,
  ],

  // Icon
  manufacturerLight: [
    `0 0 0 1px ${fade(palette.gray900, 0.06)}`,
    `0px 1px 0px ${fade(palette.gray900, 0.06)}`,
    `0px 2px 1px ${fade(palette.gray900, 0.05)}`,
  ],
  manufacturerDark: [
    `0 0 0 1px ${fade(palette.black, 0.85)}`,
    `0 1px 0 0 ${fade(palette.black, 0.7)}`,
    `0 2px 1px 0 ${fade(palette.black, 0.5)}`,
  ],
};

export const shadows = createShadowAnimationStack(shadowParts);
